import { useMemo, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDroppable } from '@dnd-kit/core';
import { api } from '@/lib/api';
import { weekMondayFromOffset, type Block } from '@focus/shared';
import type { ActivityWindow } from '@focus/shared';
import { useUI, visibleHourRange } from '@/stores/ui';
import { Icon } from './icon';
import { cn } from '@/lib/cn';
import { NowLine } from './now-line';
import { SleepOverlay, useSleepSegments, type SleepSegment } from './sleep-overlay';
import { toneFor, type BlockTone } from './block-tones';
import { labelFor, toneForLabel, type AppTone } from './app-colors';

const MIN_HOUR_H = 28;
const GUTTER_W_DESKTOP = 72;
const GUTTER_W_MOBILE = 44;
const WEEKDAY_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function useIsMobile() {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

// ─── Adult filter (mirror of actual-view) ─────────────────────────────
const ADULT_RX = /(porn|xhamster|xvideos|eporner|redtube|xnxx|youporn|spankbang|chaturbate|stripchat|onlyfans|fapvid|fapster|fapello|nhentai|hentai|rule34|motherless|tnaflix|hclips|beeg|camsoda|bongacams|kinkvids|tube8|justporn|pornxp|sexyporn|pornoperso|theporndude|4kporno|candy\.ai|questpiracy|nudefab|nude|masturba|\bxxx\b|\bnsfw\b|\berotic|\bfetish|\bbdsm)/i;

function isAdultEntry(app: string | null, title: string | null): boolean {
  return ADULT_RX.test(`${app ?? ''} ${title ?? ''}`);
}
function sanitizeWindow(w: ActivityWindow): ActivityWindow {
  const clean = w.breakdown.filter((b) => !isAdultEntry(b.app, b.title));
  if (clean.length === w.breakdown.length) return w;
  if (clean.length === 0) {
    const totalSec = w.breakdown.reduce((s, b) => s + b.sec, 0);
    return { ...w, dominantApp: 'Privat', dominantTitle: null, breakdown: [{ app: 'Privat', title: null, sec: totalSec }] };
  }
  const top = clean[0];
  return { ...w, breakdown: clean, dominantApp: top.app, dominantTitle: top.title };
}

function isInSleep(hour: number, dayIdx: number, segs: SleepSegment[]): boolean {
  return segs.some((s) => s.dayIdx === dayIdx && hour >= s.sHour && hour < s.eHour);
}

// ─── Merge helper ─────────────────────────────────────────────────────
type MergedChunk = {
  bucket: number;
  quartersCount: number;
  totalDurationMin: number;
  label: string;
  appHint: string;
};
function chunkLabel(w: ActivityWindow): string {
  const top = w.breakdown[0];
  if (top) return labelFor(top.app, top.title);
  return labelFor(w.dominantApp, null);
}
function mergeWindows(windows: ActivityWindow[]): MergedChunk[] {
  if (!windows.length) return [];
  const sorted = [...windows].sort((a, b) => a.bucket - b.bucket);
  const out: MergedChunk[] = [];
  let cur: MergedChunk | null = null;
  for (const w of sorted) {
    const label = chunkLabel(w);
    const adjacent = cur && Math.abs(cur.bucket + cur.quartersCount * 0.25 - w.bucket) < 0.001;
    if (cur && cur.label === label && adjacent) {
      cur.quartersCount += 1;
      cur.totalDurationMin += w.durationMin;
    } else {
      cur = {
        bucket: w.bucket,
        quartersCount: 1,
        totalDurationMin: w.durationMin,
        label,
        appHint: w.breakdown[0]?.app ?? w.dominantApp,
      };
      out.push(cur);
    }
  }
  return out;
}

// ─── On-task classification ───────────────────────────────────────────
// A chunk is "on-task" if it overlaps any non-buffer plan block AND
// its label fits the plan category (heuristic: blockDeep = code/editor/Claude/ChatGPT,
// blockMeeting = Zoom/Teams/Meet, blockAdmin = Mail/Notion/Admin stuff).
const ON_TASK_HINTS: Record<string, RegExp> = {
  blockDeep: /claude|chatgpt|gemini|copilot|code|cursor|xcode|intellij|webstorm|pycharm|android studio|ghostty|terminal|warp|iterm/i,
  blockMeeting: /zoom|teams|meet|webex/i,
  blockAdmin: /gmail|mail\b|notion|google workspace|docs|sheets|slides|kalender|calendar/i,
  blockCreative: /figma|sketch|photoshop|illustrator|adobe|blender/i,
  blockLearning: /anthropic|medium|wikipedia|stack overflow|hacker news/i,
  blockSocial: /slack|discord|whatsapp|linkedin|instagram|telegram|messenger|signal/i,
};

type MatchState = 'ontask' | 'offtask' | 'neutral';
function classifyChunk(c: MergedChunk, blocks: Block[]): { state: MatchState; planCat: string | null } {
  const cEnd = c.bucket + c.quartersCount * 0.25;
  const overlapping = blocks.filter((b) => b.e > c.bucket && b.s < cEnd);
  if (overlapping.length === 0) return { state: 'neutral', planCat: null };
  // Pick the block with the largest overlap.
  const scored = overlapping.map((b) => {
    const ovStart = Math.max(b.s, c.bucket);
    const ovEnd = Math.min(b.e, cEnd);
    return { b, ov: Math.max(0, ovEnd - ovStart) };
  });
  scored.sort((a, b) => b.ov - a.ov);
  const plan = scored[0].b;
  const hint = ON_TASK_HINTS[plan.cat];
  if (!hint) return { state: 'neutral', planCat: plan.cat };
  const text = `${c.label} ${c.appHint}`;
  return { state: hint.test(text) ? 'ontask' : 'offtask', planCat: plan.cat };
}

// ─── Hybrid Day View ──────────────────────────────────────────────────
export function HybridDayView() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const GUTTER_W = isMobile ? GUTTER_W_MOBILE : GUTTER_W_DESKTOP;
  const {
    darkMode,
    dayOffset, goPrevDay, goNextDay, goToday,
    quietHoursEnabled, quietHoursFrom, quietHoursTo,
    zoomLevel,
    appColorOverrides,
    openModal,
  } = useUI();
  const { startHour: START_HOUR, endHour: END_HOUR } = visibleHourRange({ quietHoursEnabled, quietHoursFrom, quietHoursTo });
  const HOURS = END_HOUR - START_HOUR;

  const viewDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);
  const weekStart = useMemo(() => {
    const nd = new Date(viewDate);
    const dow = nd.getDay();
    nd.setDate(nd.getDate() + (dow === 0 ? -6 : 1 - dow));
    return nd.getTime();
  }, [viewDate]);
  const dayIdx = (viewDate.getDay() + 6) % 7;

  const { data: blocks = [] } = useQuery({
    queryKey: ['blocks', weekStart],
    queryFn: () => api.listBlocks(weekStart),
  });
  const { data: activity } = useQuery({
    queryKey: ['activity', weekStart, dayIdx],
    queryFn: () => api.getActivityWindows({ weekStart, dayIdx, bucketMin: 15 }),
    refetchInterval: 30000,
  });
  const sleepSegments = useSleepSegments(weekStart);

  const patchBlock = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Block> }) => api.patchBlock(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', weekStart] }),
  });

  const dayBlocks = blocks.filter((b) => b.dayIdx === dayIdx && b.e > START_HOUR && b.s < END_HOUR);
  const allWindows: ActivityWindow[] = activity?.days?.[dayIdx] ?? [];
  const chunks = useMemo(() => {
    const filtered = allWindows
      .filter((w) => w.bucket + 0.25 > START_HOUR && w.bucket < END_HOUR)
      .filter((w) => !isInSleep(w.bucket + 0.125, dayIdx, sleepSegments))
      .map(sanitizeWindow);
    return mergeWindows(filtered);
  }, [allWindows, START_HOUR, END_HOUR, sleepSegments, dayIdx]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [baseHourH, setBaseHourH] = useState(MIN_HOUR_H);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => setBaseHourH(Math.max(MIN_HOUR_H, Math.floor(el.clientHeight / HOURS)));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [HOURS]);
  const hourH = Math.max(MIN_HOUR_H, Math.round(baseHourH * zoomLevel));
  const totalH = HOURS * hourH;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = viewDate.getTime() === today.getTime();

  const stats = useMemo(() => summarize(chunks, dayBlocks), [chunks, dayBlocks]);

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div className="grid border-b border-line dark:border-dark-line" style={{ gridTemplateColumns: `${GUTTER_W}px 1fr` }}>
        <div />
        <div className="px-2 md:px-4 py-2 md:py-2.5 flex items-center gap-2 md:gap-3 border-l border-line dark:border-dark-line">
          <span className="text-[16px] md:text-[18px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg">
            {viewDate.getDate()}.
          </span>
          <span className="text-[12.5px] md:text-[14px] font-medium tracking-[-0.1px] text-fg dark:text-dark-fg truncate">
            {WEEKDAY_LONG[dayIdx]}
          </span>
          <span className="hidden md:inline text-[12px] font-mono text-fg dark:text-dark-fg">
            Mix · Plan als Rahmen, Ist drauf
          </span>
          <div className="hidden md:block"><OnTaskMiniStats stats={stats} /></div>
          <div className="ml-auto flex gap-1">
            <button onClick={goPrevDay} className="btn-ghost h-8 px-2" title="Tag zurück"><Icon name="chevronLeft" size={14} /></button>
            <button onClick={goToday} className="btn-ghost h-8 px-2 text-[12px]" title="Heute">Heute</button>
            <button onClick={goNextDay} className="btn-ghost h-8 px-2" title="Tag vor"><Icon name="chevronRight" size={14} /></button>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin">
        <div className="grid relative pt-3" style={{ gridTemplateColumns: `${GUTTER_W}px 1fr`, minHeight: '100%' }}>
          <GutterHours totalH={totalH} hourH={hourH} startHour={START_HOUR} endHour={END_HOUR} />
          <HybridColumn
            dayIdx={dayIdx}
            weekStart={weekStart}
            blocks={dayBlocks}
            chunks={chunks}
            sleepSegments={sleepSegments}
            startHour={START_HOUR}
            endHour={END_HOUR}
            hourH={hourH}
            totalH={totalH}
            darkMode={darkMode}
            appColorOverrides={appColorOverrides}
            isToday={isToday}
            onToggleDone={(id, done) => patchBlock.mutate({ id, patch: { done } })}
            onOpenBlock={(id) => openModal('blockDetail', id)}
            compact={false}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Hybrid Week View ─────────────────────────────────────────────────
export function HybridWeekView() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const GUTTER_W = isMobile ? GUTTER_W_MOBILE : GUTTER_W_DESKTOP;
  const {
    weekOffset, darkMode,
    quietHoursEnabled, quietHoursFrom, quietHoursTo,
    zoomLevel,
    appColorOverrides,
    openModal,
  } = useUI();
  const { startHour: START_HOUR, endHour: END_HOUR } = visibleHourRange({ quietHoursEnabled, quietHoursFrom, quietHoursTo });
  const HOURS = END_HOUR - START_HOUR;
  const weekStart = useMemo(() => weekMondayFromOffset(weekOffset).getTime(), [weekOffset]);

  const { data: blocks = [] } = useQuery({
    queryKey: ['blocks', weekStart],
    queryFn: () => api.listBlocks(weekStart),
  });
  const { data: activity } = useQuery({
    queryKey: ['activity', weekStart],
    queryFn: () => api.getActivityWindows({ weekStart, bucketMin: 15 }),
    refetchInterval: 30000,
  });
  const sleepSegments = useSleepSegments(weekStart);

  const patchBlock = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Block> }) => api.patchBlock(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blocks', weekStart] }),
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [baseHourH, setBaseHourH] = useState(MIN_HOUR_H);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => setBaseHourH(Math.max(MIN_HOUR_H, Math.floor(el.clientHeight / HOURS)));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [HOURS]);
  const hourH = Math.max(MIN_HOUR_H, Math.round(baseHourH * zoomLevel));
  const totalH = HOURS * hourH;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monday = new Date(weekStart);
  const todayIdx = today >= monday && today.getTime() < weekStart + 7 * 86400000 ? (today.getDay() + 6) % 7 : -1;

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div className="grid border-b border-line dark:border-dark-line" style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)` }}>
        <div />
        {(isMobile ? WEEKDAY_SHORT : WEEKDAY_LONG).map((name, i) => {
          const d = new Date(weekStart + i * 86400000);
          return (
            <div key={i} className="px-1 md:px-3 py-1.5 md:py-2.5 flex flex-col md:flex-row items-center md:items-baseline gap-0 md:gap-2 border-l border-line dark:border-dark-line truncate">
              <span className="text-[12px] md:text-[15px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg">
                {d.getDate()}.
              </span>
              <span className="text-[10px] md:text-[12.5px] font-medium tracking-[-0.1px] truncate text-fg dark:text-dark-fg">
                {name}
              </span>
            </div>
          );
        })}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin">
        <div className="grid relative pt-3" style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)`, minHeight: '100%' }}>
          <GutterHours totalH={totalH} hourH={hourH} startHour={START_HOUR} endHour={END_HOUR} />
          {Array.from({ length: 7 }, (_, di) => {
            const dayBlocks = blocks.filter((b) => b.dayIdx === di && b.e > START_HOUR && b.s < END_HOUR);
            const allW = (activity?.days?.[di] ?? []) as ActivityWindow[];
            const chunks = mergeWindows(
              allW
                .filter((w) => w.bucket + 0.25 > START_HOUR && w.bucket < END_HOUR)
                .filter((w) => !isInSleep(w.bucket + 0.125, di, sleepSegments))
                .map(sanitizeWindow),
            );
            return (
              <HybridColumn
                key={di}
                dayIdx={di}
                weekStart={weekStart}
                blocks={dayBlocks}
                chunks={chunks}
                sleepSegments={sleepSegments}
                startHour={START_HOUR}
                endHour={END_HOUR}
                hourH={hourH}
                totalH={totalH}
                darkMode={darkMode}
                appColorOverrides={appColorOverrides}
                isToday={di === todayIdx}
                onToggleDone={(id, done) => patchBlock.mutate({ id, patch: { done } })}
                onOpenBlock={(id) => openModal('blockDetail', id)}
                compact
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Column (shared) ──────────────────────────────────────────────────
function HybridColumn({
  dayIdx,
  weekStart,
  blocks,
  chunks,
  sleepSegments,
  startHour,
  endHour,
  hourH,
  totalH,
  darkMode,
  appColorOverrides,
  isToday,
  onToggleDone,
  onOpenBlock,
  compact,
}: {
  dayIdx: number;
  weekStart: number;
  blocks: Block[];
  chunks: MergedChunk[];
  sleepSegments: SleepSegment[];
  startHour: number;
  endHour: number;
  hourH: number;
  totalH: number;
  darkMode: boolean;
  appColorOverrides: Record<string, string>;
  isToday: boolean;
  onToggleDone: (id: string, done: boolean) => void;
  onOpenBlock: (id: string) => void;
  compact: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `hybrid-day:${dayIdx}`,
    data: { type: 'day', weekStart, dayIdx, hourH, startHour, endHour },
  });
  const colRef = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    setNodeRef(el);
    colRef.current = el;
  }, [setNodeRef]);

  return (
    <div
      ref={setRef}
      className={cn('relative border-l border-line dark:border-dark-line', isOver && 'bg-accentSoft/40 dark:bg-accent/10')}
      style={{ ['--cal-row-h' as any]: `${hourH}px`, height: totalH } as React.CSSProperties}
    >
      <div className="absolute inset-0 cal-grid-line pointer-events-none" />
      <SleepOverlay segments={sleepSegments} dayIdx={dayIdx} hourH={hourH} startHour={startHour} endHour={endHour} />

      {/* Layer 1: ghost plan blocks */}
      {blocks.map((b) => (
        <GhostBlock
          key={b.id}
          block={b}
          darkMode={darkMode}
          hourH={hourH}
          startHour={startHour}
          compact={compact}
          onToggleDone={(done) => onToggleDone(b.id, done)}
          onOpen={() => onOpenBlock(b.id)}
        />
      ))}

      {/* Layer 2: ist chunks on top */}
      {chunks.map((c) => (
        <ChunkLayer
          key={`${c.bucket}-${c.quartersCount}-${c.label}`}
          chunk={c}
          blocks={blocks}
          hourH={hourH}
          startHour={startHour}
          darkMode={darkMode}
          overrides={appColorOverrides}
          compact={compact}
        />
      ))}

      {isToday && <NowLine startHour={startHour} endHour={endHour} hourH={hourH} />}
    </div>
  );
}

// ─── Ghost plan block (outline only, non-interactive) ─────────────────
function GhostBlock({
  block, darkMode, hourH, startHour, compact, onToggleDone, onOpen,
}: {
  block: Block;
  darkMode: boolean;
  hourH: number;
  startHour: number;
  compact: boolean;
  onToggleDone: (done: boolean) => void;
  onOpen: () => void;
}) {
  const top = (block.s - startHour) * hourH;
  const height = Math.max(6, (block.e - block.s) * hourH);
  const tone: BlockTone = toneFor(block.cat);
  const line = darkMode ? tone.lineDark : tone.line;
  const ink = darkMode ? tone.inkDark : tone.ink;
  const fill = darkMode ? tone.bgDark : tone.bg;
  const isTiny = height < 18;
  const isShort = height < 40;
  const catLabel = tone.label;

  return (
    <div
      className={cn(
        'absolute rounded-md overflow-hidden cursor-pointer hover:brightness-[0.98] dark:hover:brightness-110 transition',
        compact ? 'left-0.5 right-0.5' : 'left-1 right-1',
      )}
      style={{
        top,
        height,
        background: fill,
        border: `1px solid ${line}`,
        borderLeft: `3px solid ${line}`,
      }}
      aria-label={`Termin: ${block.t}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('input, label, a')) return;
        onOpen();
      }}
    >
      {!isTiny && (
        <div
          className={cn('absolute left-0 right-0 px-1.5', compact ? 'top-0.5' : 'top-1')}
          style={{ color: ink, pointerEvents: 'none' }}
        >
          <div className="flex items-center gap-1 text-[11px] font-semibold tracking-[-0.1px] leading-tight">
            {block.meetingUrl && <Icon name="link" size={10} className="opacity-80 shrink-0" />}
            <span className="truncate min-w-0 pointer-events-auto">
              {compact ? (
                <span className={cn(block.done && 'line-through opacity-60')}>{block.t}</span>
              ) : (
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={block.done}
                    onChange={(e) => onToggleDone(e.target.checked)}
                    className="h-3 w-3 accent-accent"
                  />
                  <span className={cn(block.done && 'line-through opacity-60')}>{block.t}</span>
                </label>
              )}
            </span>
            <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.5px] opacity-55 shrink-0">
              {catLabel}
            </span>
          </div>
          {!isShort && (
            <div className="text-[9.5px] font-mono opacity-60 mt-[1px]">
              {fmtHour(block.s)}–{fmtHour(block.e)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Activity chunk with on-task match border ─────────────────────────
function ChunkLayer({
  chunk, blocks, hourH, startHour, darkMode, overrides, compact,
}: {
  chunk: MergedChunk;
  blocks: Block[];
  hourH: number;
  startHour: number;
  darkMode: boolean;
  overrides: Record<string, string>;
  compact: boolean;
}) {
  const [popover, setPopover] = useState<{ x: number; y: number } | null>(null);
  const cls = classifyChunk(chunk, blocks);
  const top = (chunk.bucket - startHour) * hourH;
  const capacityH = chunk.quartersCount * 0.25 * hourH;
  const height = Math.max(10, capacityH) - 1;
  const tone: AppTone = toneForLabel(chunk.label, overrides);
  const bg = darkMode ? tone.bgDark : tone.bg;
  const ink = darkMode ? tone.inkDark : tone.ink;

  const borderColor =
    cls.state === 'ontask' ? 'oklch(0.65 0.18 148)' :
    cls.state === 'offtask' ? 'oklch(0.63 0.20 25)' :
    'transparent';
  const borderWidth = cls.state === 'neutral' ? 0 : 2;

  const fontPx = height >= 36 ? 11 : height >= 20 ? 10 : 9;
  const showTime = !compact && height >= 18;
  const showMin = !compact && height >= 28;

  return (
    <>
      <div
        className={cn(
          'absolute rounded-[4px] overflow-hidden cursor-pointer z-10',
          compact ? 'left-1.5 right-1.5 px-1' : 'left-3 right-3 px-2',
        )}
        style={{
          top, height,
          background: bg,
          color: ink,
          boxShadow: borderWidth > 0 ? `inset 0 0 0 ${borderWidth}px ${borderColor}` : undefined,
        }}
        title={`${chunk.label} · ${chunk.totalDurationMin} min · ${cls.state === 'ontask' ? 'on-task' : cls.state === 'offtask' ? `off-task (Plan: ${cls.planCat ?? '?'})` : 'kein Plan'}`}
        onClick={(e) => setPopover({ x: e.clientX, y: e.clientY })}
      >
        <div
          className="absolute inset-0 flex items-center justify-center gap-1.5 px-1 leading-tight font-medium whitespace-nowrap overflow-hidden"
          style={{ fontSize: `${fontPx}px` }}
        >
          {showTime && <span className="font-mono opacity-70 tabular-nums">{fmtHour(chunk.bucket)}</span>}
          <span className="truncate">{chunk.label}</span>
          {showMin && <span className="font-mono opacity-60 tabular-nums">· {chunk.totalDurationMin}m</span>}
        </div>
      </div>
      {popover && (
        <ChunkMiniPopover
          chunk={chunk}
          cls={cls}
          x={popover.x}
          y={popover.y}
          onClose={() => setPopover(null)}
        />
      )}
    </>
  );
}

function ChunkMiniPopover({
  chunk, cls, x, y, onClose,
}: {
  chunk: MergedChunk;
  cls: { state: MatchState; planCat: string | null };
  x: number; y: number; onClose: () => void;
}) {
  const endHour = chunk.bucket + chunk.quartersCount * 0.25;
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 rounded-lg shadow-pop w-[300px] overflow-hidden bg-paper dark:bg-dark-paper border border-line dark:border-dark-line"
        style={{ left: Math.min(x, window.innerWidth - 320), top: Math.min(y, window.innerHeight - 200) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-line dark:border-dark-line">
          <div className="font-semibold text-[13px] tracking-[-0.1px]">{chunk.label}</div>
          <div className="font-mono text-[11px] tabular-nums opacity-80 mt-0.5">
            {fmtHour(chunk.bucket)} – {fmtHour(endHour)} · {chunk.totalDurationMin} min
          </div>
        </div>
        <div className="px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-fg dark:text-dark-fg mb-1.5">Plan-Abgleich</div>
          {cls.state === 'ontask' && (
            <div className="text-[12px] font-medium" style={{ color: 'oklch(0.55 0.18 148)' }}>
              ✓ On-task zum Plan „{cls.planCat}"
            </div>
          )}
          {cls.state === 'offtask' && (
            <div className="text-[12px] font-medium" style={{ color: 'oklch(0.60 0.20 25)' }}>
              ✗ Off-task. Plan war „{cls.planCat}".
            </div>
          )}
          {cls.state === 'neutral' && (
            <div className="text-[12px] text-fg dark:text-dark-fg">Kein Plan-Block in dieser Zeit.</div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Gutter hours ─────────────────────────────────────────────────────
function GutterHours({ totalH, hourH, startHour, endHour }: { totalH: number; hourH: number; startHour: number; endHour: number }) {
  const HOURS = endHour - startHour;
  return (
    <div className="relative border-r border-line dark:border-dark-line bg-paper dark:bg-dark-paper" style={{ height: totalH }}>
      {Array.from({ length: Math.floor(HOURS) + 1 }, (_, i) => {
        const hour = Math.floor(startHour) + i;
        if (hour > endHour) return null;
        const top = (hour - startHour) * hourH;
        return (
          <div
            key={hour}
            className="absolute left-0 right-0 flex items-center justify-end pr-1 md:pr-2 pointer-events-none"
            style={{ top, height: 0 }}
          >
            <span className="text-[10px] md:text-[12px] font-mono text-black dark:text-white tabular-nums leading-none bg-paper dark:bg-dark-paper px-1.5 md:px-3 py-0.5">
              {String(hour).padStart(2, '0')}:00
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── On-task mini stats (header badge in day view) ────────────────────
function summarize(chunks: MergedChunk[], blocks: Block[]) {
  let on = 0, off = 0, free = 0;
  for (const c of chunks) {
    const cls = classifyChunk(c, blocks);
    const m = c.totalDurationMin;
    if (cls.state === 'ontask') on += m;
    else if (cls.state === 'offtask') off += m;
    else free += m;
  }
  const total = on + off + free || 1;
  return {
    onMin: on, offMin: off, freeMin: free,
    onPct: Math.round((on / total) * 100),
    offPct: Math.round((off / total) * 100),
  };
}
function OnTaskMiniStats({ stats }: { stats: ReturnType<typeof summarize> }) {
  if (stats.onMin + stats.offMin + stats.freeMin === 0) return null;
  return (
    <div className="flex items-center gap-2 ml-2 text-[11px] font-mono">
      <span className="px-1.5 py-0.5 rounded" style={{ background: 'oklch(0.94 0.06 148)', color: 'oklch(0.38 0.12 148)' }}>
        on {stats.onPct}%
      </span>
      <span className="px-1.5 py-0.5 rounded" style={{ background: 'oklch(0.94 0.06 25)', color: 'oklch(0.38 0.13 25)' }}>
        off {stats.offPct}%
      </span>
    </div>
  );
}

function fmtHour(h: number) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
